// Recruiting — Templates card (docs/RECRUITING-PLAN.md item 5, ruling 15:
// "company templates by Tomer plus each manager's own"). Sits on the
// nvRecruiter page (RecruitingBoard.tsx mounts it above the day view).
//
// Two lists: COMPANY (visible to everyone, writable by OWNER ONLY — the
// server contract is explicit about this even though the ruling's own
// prose says "Tomer's"; a DIRECTOR sees the company list read-only, no
// Add/Edit/Remove, because the server would 403 those anyway — see
// src/recruiting_email.js's templateWriteRefusal) and MINE (each viewer's
// own, always writable by its owner). Same add/edit/remove shape as
// client/src/pages/Logins.tsx.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { useResource, refetchResource } from '../../lib/store';
import { EmptyState, FaultState } from '../FaultState';
import { useWrite } from '../MoneyForms';
import {
  RECRUITING_TEMPLATES_KEY, fetchRecruitingTemplates, type RecruitingTemplate,
} from '../../lib/recruitingApi';
import {
  createRecruitingTemplate, updateRecruitingTemplate, removeRecruitingTemplate,
} from '../../lib/recruitingWrite';
import type { RecruitingViewer } from './actions/CandidateActions';

interface EditorState {
  scope: 'COMPANY' | 'MINE';
  id: number | null;
  name: string;
  subject: string;
  body: string;
}

function TemplateList(
  {
    title, rows, canWrite, busy, onAdd, onEdit, onRemove, editor,
  }: {
    title: string;
    rows: RecruitingTemplate[];
    canWrite: boolean;
    busy: boolean;
    onAdd: () => void;
    onEdit: (t: RecruitingTemplate) => void;
    onRemove: (t: RecruitingTemplate) => void;
    /** The add/edit form, shown inside THIS list (owner, 2026-09-28: the form
     * sat below both lists, side by side and cramped). */
    editor: JSX.Element | null;
  },
): JSX.Element {
  return (
    <section data-testid={`recruiting-template-list-${title === 'Company templates' ? 'company' : 'mine'}`}>
      <div class="section-head-row">
        <h3>{title}</h3>
        {canWrite && !editor ? (
          <button type="button" class="btn" disabled={busy} onClick={onAdd} data-testid={`recruiting-template-add-${title === 'Company templates' ? 'company' : 'mine'}`}>
            Add template
          </button>
        ) : null}
      </div>
      {editor}
      {rows.length === 0 ? <p class="muted recruiting-template-empty">No templates yet.</p> : (
        <ul class="recruiting-template-list">
          {rows.map((t) => (
            <li key={t.id} data-testid={`recruiting-template-${t.id}`}>
              <span class="recruiting-template-name">{t.name}</span>
              <span class="muted"> — {t.subject || '(no subject)'}</span>
              {canWrite ? (
                <span class="actions">
                  <button type="button" class="btn" disabled={busy} onClick={() => onEdit(t)} data-testid={`recruiting-template-edit-${t.id}`}>
                    Edit
                  </button>
                  <button type="button" class="btn" disabled={busy} onClick={() => onRemove(t)} data-testid={`recruiting-template-remove-${t.id}`}>
                    Remove
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function RecruitingTemplates({ viewer }: { viewer: RecruitingViewer }): JSX.Element {
  const load = useResource(RECRUITING_TEMPLATES_KEY, fetchRecruitingTemplates);
  const [editing, setEditing] = useState<EditorState | null>(null);
  const write = useWrite();

  const canWriteCompany = viewer.role === 'OWNER';

  function startAdd(scope: 'COMPANY' | 'MINE'): void {
    setEditing({ scope, id: null, name: '', subject: '', body: '' });
  }
  function startEdit(t: RecruitingTemplate): void {
    setEditing({ scope: t.scope, id: t.id, name: t.name, subject: t.subject, body: t.body });
  }
  function cancelEdit(): void {
    setEditing(null);
  }

  async function save(): Promise<void> {
    if (!editing || editing.name.trim() === '') return;
    await write.run(async () => {
      if (editing.id) {
        await updateRecruitingTemplate(editing.id, {
          name: editing.name.trim(), subject: editing.subject, body: editing.body,
        });
      } else {
        await createRecruitingTemplate({
          scope: editing.scope, name: editing.name.trim(), subject: editing.subject, body: editing.body,
        });
      }
      setEditing(null);
      await refetchResource(RECRUITING_TEMPLATES_KEY, fetchRecruitingTemplates);
    });
  }

  async function remove(t: RecruitingTemplate): Promise<void> {
    await write.run(async () => {
      await removeRecruitingTemplate(t.id);
      if (editing?.id === t.id) setEditing(null);
      await refetchResource(RECRUITING_TEMPLATES_KEY, fetchRecruitingTemplates);
    });
  }

  if (load.status === 'loading') {
    return (
      <section class="card" data-testid="recruiting-templates">
        <h2>Templates</h2>
        <EmptyState label="Loading templates…" />
      </section>
    );
  }
  if (load.status === 'fault') {
    return (
      <section class="card" data-testid="recruiting-templates">
        <h2>Templates</h2>
        <FaultState name="recruiting_templates_load_failed" detail={load.error} />
      </section>
    );
  }

  const templates = load.data.templates;
  const company = templates.filter((t) => t.scope === 'COMPANY');
  const mine = templates.filter((t) => t.scope === 'MINE' && t.ownerEmail === viewer.email);

  const editorEl = editing ? (
        <div class="recruiting-template-editor" data-testid="recruiting-template-editor">
          <label class="nh-field">
            <span class="nh-label">Name</span>
            <input
              type="text" aria-label="Template name" value={editing.name} disabled={write.busy}
              onInput={(e: Event) => setEditing((cur) => (cur ? { ...cur, name: (e.target as HTMLInputElement).value } : cur))}
            />
          </label>
          <label class="nh-field">
            <span class="nh-label">Subject</span>
            <input
              type="text" aria-label="Template subject" value={editing.subject} disabled={write.busy}
              onInput={(e: Event) => setEditing((cur) => (cur ? { ...cur, subject: (e.target as HTMLInputElement).value } : cur))}
            />
          </label>
          <label class="nh-field">
            <span class="nh-label">Body</span>
            <textarea
              class="notes-entry" rows={10} aria-label="Template body" value={editing.body} disabled={write.busy}
              onInput={(e: Event) => setEditing((cur) => (cur ? { ...cur, body: (e.target as HTMLTextAreaElement).value } : cur))}
            />
          </label>
          <div class="actions">
            <button
              type="button" class="btn primary" disabled={write.busy || editing.name.trim() === ''}
              onClick={() => void save()} data-testid="recruiting-template-save"
            >
              {write.busy ? 'Saving…' : editing.id ? 'Save' : 'Add'}
            </button>
            <button type="button" class="btn" disabled={write.busy} onClick={cancelEdit} data-testid="recruiting-template-cancel">
              Cancel
            </button>
          </div>
        </div>
  ) : null;

  return (
    <section class="card" data-testid="recruiting-templates">
      <h2>Templates</h2>

      <TemplateList
        title="Company templates" rows={company} canWrite={canWriteCompany} busy={write.busy}
        onAdd={() => startAdd('COMPANY')} onEdit={startEdit} onRemove={(t) => void remove(t)}
        editor={editing?.scope === 'COMPANY' ? editorEl : null}
      />
      <TemplateList
        title="My templates" rows={mine} canWrite busy={write.busy}
        onAdd={() => startAdd('MINE')} onEdit={startEdit} onRemove={(t) => void remove(t)}
        editor={editing?.scope === 'MINE' ? editorEl : null}
      />

      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </section>
  );
}
